/**
 * ONE source of truth for the document font.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The `@font-face` block used to be copy-pasted into `a4Stylesheet.ts` and
 * `thermalStylesheet.ts`. That is the exact hazard `a4Stylesheet.ts` warns
 * about in its own header - "a tenth styling authority is exactly how two
 * authorities come to disagree" - applied to the one declaration that decides
 * every glyph's width. Two copies of the font declaration is two places for
 * the metrics to drift apart, and glyph metrics are the mechanism behind the
 * original 1.6.x border-misalignment bug.
 *
 * A FALLBACK IS NOT PREVENTABLE, ONLY DETECTABLE
 * ----------------------------------------------
 * Both stylesheets used to claim "NO fallback" in a comment while their
 * `font-family` rule read `'DocSans', sans-serif`. The comment was false and
 * the rule was harmless, but it taught the reader a wrong thing.
 *
 * The truth: omitting the fallback does NOT stop substitution. CSS always
 * falls back to the user agent's default font when a family is unavailable;
 * listing nothing just makes that substitution *invisible* instead of
 * *documented*. There is no syntax for "refuse to render if this font is
 * missing". So the guarantee is not written in the CSS - it is written in
 * `assertDocFontsLoaded()`, which the print and PDF paths call BEFORE they
 * print. A document that would print with the wrong metrics is refused rather
 * than silently misaligned.
 */

/** The family name the whole document is typeset in. */
export const DOC_FONT_FAMILY = 'DocSans';

/** The regular face. Named for the file that must exist beside it. */
export const DOC_FONT_REGULAR_FILE = 'arial.ttf';

/** The bold face. The two faces are separate files and must both resolve. */
export const DOC_FONT_BOLD_FILE = 'arialbd.ttf';

/**
 * The two files that MUST be reachable for the document to be trustworthy.
 * Exported so a build step, or a test, can assert they exist on disk.
 */
export const DOC_FONT_FILES: readonly string[] = Object.freeze([
  DOC_FONT_REGULAR_FILE,
  DOC_FONT_BOLD_FILE,
]);

/**
 * WHY THE TTF IS NOT BASE64-INLINED
 * ---------------------------------
 * `assets/arial.ttf` is 1,036,584 bytes and `arialbd.ttf` is 980,756. Base64
 * inflates by a third, so embedding both would add roughly 2.7 MB of `data:`
 * to EVERY rendered invoice, on every print, to protect a case that a five-line
 * runtime check catches. The fonts ship as real files and resolve by URL; the
 * check below is what makes that safe.
 */

/**
 * The `@font-face` rules, from the one place that defines them.
 *
 * @param assetsBaseUrl directory the two TTFs are served from, WITH a trailing
 *        slash, e.g. `new URL('assets/', location.href).href` or a
 *        `file:///.../assets/` path. An empty string yields a document-relative
 *        URL, which is only correct when the fonts genuinely sit beside the
 *        document - see `assertDocFontsLoaded`, which catches it when they do
 *        not.
 *
 * `font-display: block` is deliberate: text hidden for a few milliseconds
 * beats showing the wrong glyphs at the wrong widths first, because the print
 * may already have captured them.
 */
export function docFontFacesCss(assetsBaseUrl: string): string {
  return `
@font-face {
  font-family: '${DOC_FONT_FAMILY}';
  src: url('${assetsBaseUrl}${DOC_FONT_REGULAR_FILE}') format('truetype');
  font-weight: 400;
  font-style: normal;
  font-display: block;
}
@font-face {
  font-family: '${DOC_FONT_FAMILY}';
  src: url('${assetsBaseUrl}${DOC_FONT_BOLD_FILE}') format('truetype');
  font-weight: 700;
  font-style: normal;
  font-display: block;
}
`.trim();
}

/**
 * The `font-family` declaration.
 *
 * The `sans-serif` term is a DOCUMENTED last resort, not a silent one: it is
 * what keeps text rendering (and the page printable) on a machine that has
 * never run the app. Callers that care - which is every print and PDF path -
 * are expected to have called `assertDocFontsLoaded()` first, so this branch
 * should never be reached in practice.
 */
export function docFontFamilyCss(): string {
  return `font-family: '${DOC_FONT_FAMILY}', sans-serif;`;
}

/**
 * The string handed to `FontFaceSet.check()`.
 *
 * `check()` answers "is this family loaded AND usable at this size", a more
 * useful question than "is it in the cascade". Note the space-separated form:
 * the first token parses as a font SIZE, and passing a bare family name can
 * return true for a font that is declared but not yet loaded.
 */
export const DOC_FONT_CHECK_QUERY = `1rem ${DOC_FONT_FAMILY}`;

/** The subset of `document.fonts` this module needs. */
export interface FontFaceLike {
  readonly family: string;
  readonly weight: string;
  readonly status: string;
}

export interface FontFaceSetLike {
  check(font: string): boolean;
  // Iterability is REQUIRED, not optional - see `docFontsLoaded` for why
  // `check()` on its own cannot be trusted.
  [Symbol.iterator](): Iterator<FontFaceLike>;
}

/** Is there a face of this family, at this weight, that actually loaded? */
function hasLoadedFace(faces: FontFaceSetLike, weight: string): boolean {
  for (const face of faces) {
    if (face.family.replace(/['"]/g, '') !== DOC_FONT_FAMILY) continue;
    if (face.weight !== weight) continue;
    if (face.status === 'loaded') return true;
  }
  return false;
}

/**
 * Have BOTH faces actually loaded?
 *
 * `check()` alone is NOT enough, and this is the subtlest trap in the module.
 * `FontFaceSet.check()` returns TRUE for a family the document never declared,
 * because there is no matching FontFace object pending and "nothing pending"
 * reads as "ready". Chromium does exactly this. It means:
 *
 *   - `assertDocFontsLoaded(parentDocument.fonts)` on a page that has no
 *     DocSans `@font-face` at all would return TRUE, and the guard would be
 *     pure decoration while printing the misaligned invoice it exists to stop;
 *   - `check('1rem ThisFontDoesNotExist')` is true for the same reason, which is
 *     why it is useless as a negative control.
 *
 * The print wrapper prints into an IFRAME, so this matters: the faces live on
 * the iframe's document, and asking the wrong document is not an edge case, it
 * is the obvious mistake.
 *
 * So this asks two independent questions. `check()` says "usable at this size";
 * iteration says "declared at this weight and its bytes were actually parsed".
 * Both must hold.
 *
 * Bold is checked separately from regular because a missing `arialbd.ttf`
 * leaves headings synthesized, and synthesized metrics differ from the real
 * face - reintroducing the wrapping problem on exactly the boldest words.
 */
export function docFontsLoaded(faces: FontFaceSetLike): boolean {
  if (!faces.check(DOC_FONT_CHECK_QUERY)) return false;
  if (!faces.check(`bold 1rem ${DOC_FONT_FAMILY}`)) return false;
  return hasLoadedFace(faces, '400') && hasLoadedFace(faces, '700');
}

/**
 * Refuse to print a document whose metrics cannot be trusted.
 *
 * Call this immediately before `window.print()`, and after awaiting
 * `document.fonts.ready`. The alternative - printing anyway - produces an
 * invoice with subtly misaligned columns and no indication that anything was
 * wrong, which is the 1.6.x bug this whole module exists to prevent.
 *
 * @throws if either face failed to load.
 */
export function assertDocFontsLoaded(faces: FontFaceSetLike): void {
  if (docFontsLoaded(faces)) return;
  throw new Error(
    `The document font "${DOC_FONT_FAMILY}" did not load, so this print would use ` +
      'substitute glyphs of different widths - the defect this renderer exists to ' +
      'prevent. Printing has been refused. Check that ' +
      `${DOC_FONT_FILES.join(' and ')} are reachable from the document URL, and ` +
      'that fonts have finished loading (await document.fonts.ready) first.',
  );
}
